import { CalculatorFormValues, StayingOvernight, StayingPlace } from 'common/types';
import * as validators from 'validators';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { range } from 'utils';
import { FC, ReactNode, useEffect } from 'react';
import { WizardPageProps } from '../../WizardForm/WizardForm';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { HomeIcon, StarIcon } from '@net0-calc/common/assets/svg';
import { FieldError, Hints, StepTitle } from '@net0-calc/common/components/ui';
import gsap from 'gsap';

const optionsTranslationPrefix = 'personal.pages.placeToStay.form.field.placeToStay';

/* ── Toggle Button (Yes / No) ──────────────────────────────────────── */

interface ToggleButtonProps extends React.InputHTMLAttributes<HTMLInputElement> {
  text: string;
}

const ToggleButton: FC<ToggleButtonProps> = ({ id, text, checked, ...props }) => {
  useEffect(() => {
    if (checked) {
      gsap.fromTo(
        '#toggle-anim-' + id,
        { transform: 'translate(-10%, -25%)', scale: 1 },
        { transform: 'translate(-100%, -50%)', scale: 0, duration: 0.4, ease: 'power2.out' },
      );
    }
  }, [checked, id]);

  return (
    <ToggleRoot $isChecked={checked}>
      <ToggleAnimElement id={'toggle-anim-' + id} />
      <ToggleLabel tabIndex={0}>
        <HiddenInput tabIndex={-1} id={id} type="radio" {...props} />
        <ToggleText $isChecked={checked}>{text}</ToggleText>
      </ToggleLabel>
    </ToggleRoot>
  );
};

/* ── Accommodation Card (square, icon above text) ──────────────────── */

interface AccommodationCardProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon: ReactNode;
  title: string;
}

const AccommodationCard: FC<AccommodationCardProps> = ({ id, icon, title, checked, ...props }) => {
  useEffect(() => {
    if (checked) {
      gsap.fromTo(
        '#card-anim-' + id,
        { transform: 'translate(-10%, -25%)', scale: 1 },
        { transform: 'translate(-100%, -50%)', scale: 0, duration: 0.4, ease: 'power2.out' },
      );
    }
  }, [checked, id]);

  return (
    <CardRoot $isChecked={checked}>
      <CardAnimElement id={'card-anim-' + id} />
      <CardLabel tabIndex={0}>
        <HiddenInput tabIndex={-1} id={id} type="radio" {...props} />
        <CardIconArea $isChecked={checked}>{icon}</CardIconArea>
        <CardTitle $isChecked={checked}>{title}</CardTitle>
      </CardLabel>
    </CardRoot>
  );
};

/* ── Stars icon ────────────────────────────────────────────────────── */

const StarsGroup: FC<{ count: number }> = ({ count }) => (
  <StarsRow>
    {range(count).map(i => (
      <StarIcon key={i} />
    ))}
  </StarsRow>
);

/* ── Main Page Component ───────────────────────────────────────────── */

export const PlaceToStayPage: FC<WizardPageProps<CalculatorFormValues>> = ({ values }) => {
  const { t } = useTranslation();

  const stayingPlaceOptions = [
    {
      icon: <StarsGroup count={2} />,
      title: t(`${optionsTranslationPrefix}.options.hotel`),
      value: StayingPlace.HOTEL_2_STARS,
    },
    {
      icon: <StarsGroup count={3} />,
      title: t(`${optionsTranslationPrefix}.options.hotel`),
      value: StayingPlace.HOTEL_3_STARS,
    },
    {
      icon: <StarsGroup count={4} />,
      title: t(`${optionsTranslationPrefix}.options.hotel`),
      value: StayingPlace.HOTEL_4_STARS,
    },
    {
      icon: <StarsGroup count={5} />,
      title: t(`${optionsTranslationPrefix}.options.hotel`),
      value: StayingPlace.HOTEL_5_STARS,
    },
    {
      icon: <HomeIcon />,
      title: t(`${optionsTranslationPrefix}.options.accommodation`),
      value: StayingPlace.PRIVATE_ACCOMODATION,
    },
  ];

  const stayingOvernightOptions = [
    { title: t(`${optionsTranslationPrefix}.stayingOvernightOptions.no`), value: StayingOvernight.NO },
    { title: t(`${optionsTranslationPrefix}.stayingOvernightOptions.yes`), value: StayingOvernight.YES },
  ];

  const isStayingYes = values?.stayingOvernight === StayingOvernight.YES;

  return (
    <Root>
      <StepTitle>{t('personal.pages.placeToStay.title')}</StepTitle>
      <ToggleGroup>
        {stayingOvernightOptions.map(({ value, title }) => (
          <Field key={value} name="stayingOvernight" type="radio" value={value} validate={validators.isRequired}>
            {({ input }) => <ToggleButton id={`stayingOvernight-${value}`} text={String(title)} {...input} />}
          </Field>
        ))}
      </ToggleGroup>
      <FieldError name="stayingOvernight" />

      {isStayingYes && (
        <AccommodationSection>
          <SubQuestionTitle>{t('personal.pages.placeToStay.subQuestionTitle')}</SubQuestionTitle>
          <CardGrid>
            {stayingPlaceOptions.map(({ icon, value, title }) => (
              <Field key={value} name="stayingPlace" type="radio" value={value} validate={validators.isRequired}>
                {({ input }) => <AccommodationCard id={`${value}-option`} icon={icon} title={title} {...input} />}
              </Field>
            ))}
          </CardGrid>
          <FieldError name="stayingPlace" />
          <StyledHints hints={[t('personal.pages.placeToStay.hints.default')]} />
        </AccommodationSection>
      )}
    </Root>
  );
};

/* ── Shared ─────────────────────────────────────────────────────────── */

const HiddenInput = styled.input`
  position: absolute !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 1px !important;
  height: 1px !important;
  white-space: nowrap !important;
  clip-path: inset(100%) !important;
  clip: rect(0 0 0 0) !important;
  overflow: hidden !important;
  outline: none;
`;

const Root = styled.div`
  width: 100%;
`;

/* ── Toggle Button styles ──────────────────────────────────────────── */

const ToggleRoot = styled.div<{ $isChecked?: boolean }>`
  position: relative;
  flex: 1;
  z-index: 0;
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color10)')};
  background-color: ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color5)')};
  overflow: hidden;
  transition: border-color 0.25s ease-out, background-color 0.25s ease-out, box-shadow 0.25s ease-out;

  &:hover {
    border-color: ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color9)')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const ToggleAnimElement = styled.div`
  position: absolute;
  transform: translate(-25%, -25%);
  width: 500px;
  height: 500px;
  background-color: var(--color5);
  border-radius: 50%;
`;

const ToggleLabel = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 14px 24px;
`;

const ToggleText = styled.span<{ $isChecked?: boolean }>`
  ${TYPOGRAPHY.bodyMedium16}
  color: ${({ $isChecked }) => ($isChecked ? 'var(--color5)' : 'var(--color1)')};
  transition: color 0.25s ease-out;
  user-select: none;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 40px;

  @media ${DEVICE.laptop} {
    max-width: 420px;
    margin-bottom: 50px;
  }
`;

/* ── Accommodation Card styles ─────────────────────────────────────── */

const CardRoot = styled.div<{ $isChecked?: boolean }>`
  position: relative;
  z-index: 0;
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color10)')};
  background-color: ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color5)')};
  overflow: hidden;
  transition: border-color 0.25s ease-out, background-color 0.25s ease-out, box-shadow 0.25s ease-out;

  &:hover {
    border-color: ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color9)')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const CardAnimElement = styled.div`
  position: absolute;
  transform: translate(-25%, -25%);
  width: 500px;
  height: 500px;
  background-color: var(--color5);
  border-radius: 50%;
`;

const CardLabel = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 100%;
  padding: 20px 12px;
`;

const CardIconArea = styled.div<{ $isChecked?: boolean }>`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;

  & svg {
    & path {
      stroke: ${({ $isChecked }) => ($isChecked ? 'var(--color5)' : 'var(--color1)')};
      transition: stroke 0.25s ease-out;
    }
  }
`;

const CardTitle = styled.span<{ $isChecked?: boolean }>`
  ${TYPOGRAPHY.bodyMedium16}
  color: ${({ $isChecked }) => ($isChecked ? 'var(--color5)' : 'var(--color1)')};
  transition: color 0.25s ease-out;
  text-align: center;
`;

/* ── Stars ──────────────────────────────────────────────────────────── */

const StarsRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  justify-content: center;

  & svg {
    width: 18px;
    height: 18px;
  }
`;

/* ── Accommodation section ─────────────────────────────────────────── */

const AccommodationSection = styled.div`
  animation: fadeSlideIn 0.35s ease-out;

  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SubQuestionTitle = styled.div`
  ${TYPOGRAPHY.titleSemibold24}
  color: var(--color6);
  margin-bottom: 24px;

  @media ${DEVICE.laptop} {
    margin-bottom: 32px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media ${DEVICE.mobileL} {
    grid-template-columns: repeat(3, 1fr);
  }

  @media ${DEVICE.tablet} {
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    margin-bottom: 50px;
  }
`;

const StyledHints = styled(Hints)`
  margin-bottom: 20px;

  @media ${DEVICE.laptop} {
    margin-bottom: 70px;
  }
`;
